import { createFeature, createReducer, createSelector, Store } from '@ngrx/store';
import {
  CeilingAboveTerrainContainer,
  CeilingUnderTerrainContainer,
  ContourLca,
  ExteriorWallsAboveTerrainContainer,
  ExteriorWallsUnderTerrainContainer,
  FlatRoofAboveTerrainContainer,
  InclinedRoofAboveTerrainContainer,
  InteriorWallAboveTerrainContainer,
  InteriorWallUnderTerrainContainer,
  LcaOutput,
  LcaOutputType,
  LcaValueType,
  OutputDTO,
  OutRealEstateDTO,
  RealEstateDTO,
  RebuildEstateDTO,
  RebuildEstimationOutputDTO,
  RoofUnderTerrainContainer,
  SlabFoundationContainer,
  WindowAboveTerrainContainer,
  WindowUnderTerrainContainer,
  WithdrawDTO,
} from '@generated/generatedEntities';
import { immerOn } from 'ngrx-immer/store';
import { RealEstateActions } from '@app/core/services/store/real-estate.actions';
import { inject } from '@angular/core';
import { IframeInput, LcaBuildingElementPresetDTO, PLOT_TYPE } from '@generated/extended';
import _ from 'lodash';
import * as Sentry from '@sentry/angular';

export const realEstatesFeatureKey = 'realEstate';

export enum CALCULATION_STATE {
  INIT = 'INIT',
  NOT_CALCULATED = 'NOT_CALCULATED',
  LOADING = 'LOADING',
  READY = 'READY',
  LOADED = 'LOADED',
  ERROR = 'ERROR',
  CALLING = 'CALLING',
  CALCULATING = 'CALCULATING',
  FINAL_RESULT_LOADED = 'FINAL_RESULT_LOADED',
}

export interface RealEstateState {
  /**
   * This represents the baseline from the backend. **When working with the form this object WILL NOT be modified.**
   */
  initialRealEstate: RealEstateDTO | RebuildEstateDTO | OutRealEstateDTO | IframeInput;
  /**
   * Contains updates made by the user during the form process. **Caution** - this object is not always up to date with the actual form state.
   */
  realEstateProgress: RealEstateDTO | RebuildEstateDTO | OutRealEstateDTO | IframeInput;
  status: CALCULATION_STATE;
  estimationOutput: RebuildEstimationOutputDTO;
  withdrawDTO: WithdrawDTO;
  plots: { [P in PLOT_TYPE]: string | null }; //strings in this map are images base64 encoded!
  plotsRebuild: { [P in PLOT_TYPE]: string | null }; //strings in this map are images base64 encoded!
  greyEnergy: { [P in LcaValueType]?: number };
  rebuildGreyEnergy: { [P in LcaValueType]?: number };
  presets: LcaBuildingElementPresetDTO;
  targetType: string | null | undefined;
}

export const initialState: RealEstateState = {
  initialRealEstate: {} as OutRealEstateDTO,
  realEstateProgress: {} as OutRealEstateDTO,
  status: CALCULATION_STATE.INIT,
  estimationOutput: {} as RebuildEstimationOutputDTO,
  withdrawDTO: {} as WithdrawDTO,
  plots: { CRREM_CO2: null, CRREM_NRP: null, ANNUAL_ENERGY: null, GEAK: null },
  plotsRebuild: { CRREM_CO2: null, CRREM_NRP: null, ANNUAL_ENERGY: null, GEAK: null },
  greyEnergy: {},
  rebuildGreyEnergy: {},
  presets: { uValues: {}, data: {}, elements: {} } as LcaBuildingElementPresetDTO,
  targetType: 'CURRENT',
};

function getSum(data: { [P in LcaValueType]?: number } | null): undefined | number {
  if (!data) {
    return undefined;
  }
  return Object.values(data).reduce((a, b) => a + b, 0);
}

function aggregateGreyEnergyData(data: { [P in LcaOutputType]?: { [P in LcaValueType]?: number } }) {
  const getSum: (data: { [P in LcaOutputType]?: { [P in LcaValueType]?: number } }, keyId: LcaValueType) => number = (
    data: { [P in LcaOutputType]?: { [P in LcaValueType]?: number } },
    keyId: string,
  ) => {
    return Object.entries(data)
      .filter(([key, value]) => key !== 'OPERATION')
      .flatMap(([key, value]) => Object.entries(value))
      .filter(([key2, value2]) => key2 === keyId)
      .reduce((acc, [, value2]) => acc + (value2 as number), 0);
  };

  return {
    CO_2_TOTAL: getSum(data, 'CO_2_TOTAL'),
    CO_2_TOTAL_EBF: getSum(data, 'CO_2_TOTAL_EBF'),
    CO_2_TOTAL_EOL: getSum(data, 'CO_2_TOTAL_EOL'),
    CO_2_TOTAL_EBF_EOL: getSum(data, 'CO_2_TOTAL_EBF_EOL'),
    ENERGY_NON_RENEWABLE_TOTAL: getSum(data, 'ENERGY_NON_RENEWABLE_TOTAL'),
    ENERGY_NON_RENEWABLE_TOTAL_EBF: getSum(data, 'ENERGY_NON_RENEWABLE_TOTAL_EBF'),
    ENERGY_NON_RENEWABLE_TOTAL_EOL: getSum(data, 'ENERGY_NON_RENEWABLE_TOTAL_EOL'),
    ENERGY_NON_RENEWABLE_TOTAL_EBF_EOL: getSum(data, 'ENERGY_NON_RENEWABLE_TOTAL_EBF_EOL'),
    ENERGY_RENEWABLE_TOTAL: getSum(data, 'ENERGY_RENEWABLE_TOTAL'),
    ENERGY_RENEWABLE_TOTAL_EBF: getSum(data, 'ENERGY_RENEWABLE_TOTAL_EBF'),
    ENERGY_RENEWABLE_TOTAL_EOL: getSum(data, 'ENERGY_RENEWABLE_TOTAL_EOL'),
    ENERGY_RENEWABLE_TOTAL_EBF_EOL: getSum(data, 'ENERGY_RENEWABLE_TOTAL_EBF_EOL'),
  };
}

const reducer = createReducer(
  initialState,
  immerOn(RealEstateActions.updateRealEstate, (draft: RealEstateState, data) => {
    draft.initialRealEstate = { ...data };
    draft.realEstateProgress = { ...data };
    draft.status = CALCULATION_STATE.LOADED;
  }),
  immerOn(RealEstateActions.updateProgressOnRealEstate, (draft: RealEstateState, data) => {
    draft.realEstateProgress = { ...data };
  }),
  immerOn(RealEstateActions.resetStore, (draft: RealEstateState, data) => {
    // draft.initialRealEstate = {} as OutRealEstateDTO;
    // draft.realEstateProgress = {} as OutRealEstateDTO;
    // draft.status = CALCULATION_STATE.INIT;
    // draft.estimationOutput = {} as RebuildEstimationOutputDTO;
    // draft.withdrawDTO = {} as WithdrawDTO;
    // draft.plots = { CRREM_CO2: null, CRREM_NRP: null, ANNUAL_ENERGY: null, GEAK: null };
    // draft.greyEnergy = {};
    // console.log('resetStore');
    draft = { ...initialState };
  }),
  immerOn(RealEstateActions.loadRealEstate, (draft, data) => {
    // realEstate: draft.realEstate,
    draft.status = CALCULATION_STATE.LOADING;
  }),
  immerOn(RealEstateActions.getEmptyRealEstate, (draft, data) => {
    // realEstate: draft.realEstate,
    draft.status = CALCULATION_STATE.LOADING;
  }),
  immerOn(RealEstateActions.noopDueToStatus, (draft, data) => {
    console.log('noopDueToStatus');
  }),

  immerOn(RealEstateActions.loadRealEstateSuccess, (draft: RealEstateState, data) => {
    draft.initialRealEstate = { ...data } as OutRealEstateDTO;
    draft.realEstateProgress = { ...data };
    draft.status = CALCULATION_STATE.LOADED;
  }),

  immerOn(RealEstateActions.loadRealEstateFailure, (draft: RealEstateState, data) => {
    draft.initialRealEstate = {} as OutRealEstateDTO;
    draft.status = CALCULATION_STATE.ERROR;
  }),

  immerOn(RealEstateActions.estimateLCACosts, (draft: RealEstateState, data) => {
    draft.status = CALCULATION_STATE.CALCULATING;
    if (!data.data.metaData.tasks.includes('REBUILD_COSTS')) {
      draft.realEstateProgress = data.data as RealEstateDTO;
    } else {
      draft.realEstateProgress = data.data;
    }
  }),

  immerOn(RealEstateActions.estimateLCAEnergyPerformanceFailure, (draft: RealEstateState, data) => {
    console.warn(data);
  }),

  immerOn(RealEstateActions.presetLCA, (draft: RealEstateState, data) => {
    draft.realEstateProgress = data.data;
  }),

  immerOn(RealEstateActions.presetOverhaulLCA, (draft: RealEstateState, data) => {
    draft.realEstateProgress = data.data;
  }),

  immerOn(RealEstateActions.estimateOverhaulLCAEnergyNeeds, (draft: RealEstateState, data) => {
    // We do nothin here as we update the state elsewhere
  }),

  immerOn(RealEstateActions.estimateOverhaulLCAEnergyPerformance, (draft: RealEstateState, data) => {
    // We do nothin here as we update the state elsewhere
  }),

  immerOn(RealEstateActions.presetLCANoop, (draft: RealEstateState, data) => {
    // console.warn('preconditions: ', data.preconditions);
  }),

  immerOn(RealEstateActions.presetLCASuccess, (draft: RealEstateState, data) => {
    let target;
    if (data.updateUvalueFor == 'windowsAboveTerrain') {
      target = getTarget(draft, data.targetType);
      updateWindowsAboveTerrain(target, data);
      draft.presets = data; //TODO: Double check the effects of this
      return;
    }
    if (data.updateUvalueFor == 'windowsUnderTerrain') {
      target = getTarget(draft, data.targetType);
      updateWindowsUnderTerrain(target, data);
      draft.presets = data; //TODO: Double check the effects of this
      return;
    }

    // Helper function to set default heating type and distribution
    function setDefaultHeatingType(draft: RealEstateState) {
      if (
        draft.realEstateProgress.quality.heatingContainers.length > 0 &&
        draft.realEstateProgress.quality.heatingContainers[0].heatingType == null
      ) {
        draft.realEstateProgress.quality.heatingContainers[0].heatingType = 'HEAT_PUMP_AIR';
        if (draft.realEstateProgress.quality.heatingContainers[0].distribution == null) {
          draft.realEstateProgress.quality.heatingContainers[0].distribution = 'WUE_2';
        }
      }
    }

    // Helper function to check if the facade type matches the target type
    function checkFacadeTypeEquality(draft: RealEstateState, data: LcaBuildingElementPresetDTO): boolean {
      switch (data.targetType) {
        case 'ANNEX':
          return _.isEqual(
            (draft.realEstateProgress as RebuildEstateDTO).targetAnnex!.facadeType,
            data.data.facadeType,
          );
        case 'ADDITION_STORY':
          return _.isEqual(
            (draft.realEstateProgress as RebuildEstateDTO).targetAdditionStory!.facadeType,
            data.data.facadeType,
          );
        case 'OVERHAUL':
          return _.isEqual(
            (draft.realEstateProgress as RebuildEstateDTO).targetOverhaul!.facadeType,
            data.data.facadeType,
          );
        default:
          return _.isEqual(draft.realEstateProgress.quality.facadeType, data.data.facadeType?.facadeType);
      }
    }

    // Helper function to update elements based on the target type
    function updateElements(draft: RealEstateState, data: LcaBuildingElementPresetDTO) {
      const target = getTarget(draft, data.targetType);

      if (target) {
        updateGroundFloorSlabs(target, data);
        updateExteriorWallsAboveTerrain(target, data);
        updateExteriorWallsUnderTerrain(target, data);
        updateInteriorWallsAboveTerrain(target, data);
        updateCeilingsAboveTerrain(target, data);
        updateCeilingsUnderTerrain(target, data);
        updateFlatRoofsAboveTerrain(target, data);
        updateInclinedRoofsAboveTerrain(target, data);
        updateRoofsUnderTerrain(target, data);
        updateWindowsAboveTerrain(target, data);
        updateWindowsUnderTerrain(target, data);
        updateInteriorWallsUnderTerrain(target, data);
      }
    }

    // Helper function to get the target based on the target type
    function getTarget(draft: RealEstateState, targetType: string | undefined) {
      switch (targetType) {
        case 'ANNEX':
          return (draft.realEstateProgress as RebuildEstateDTO).targetAnnex;
        case 'ADDITION_STORY':
          return (draft.realEstateProgress as RebuildEstateDTO).targetAdditionStory;
        case 'OVERHAUL':
          return (draft.realEstateProgress as RebuildEstateDTO).targetOverhaul;
        case 'CURRENT':
          return draft.realEstateProgress.quality;
        default:
          return draft.realEstateProgress.quality;
      }
    }

    // Helper function to update ground floor slabs
    function updateGroundFloorSlabs(target: any, data: LcaBuildingElementPresetDTO) {
      if (!target.groundFloorSlabs) {
        target.groundFloorSlabs = [{} as SlabFoundationContainer];
      }
      for (let i = 0; i < target.groundFloorSlabs.length; i++) {
        if (data.elements?.groundFloorSlabs) {
          updateValues(
            target.groundFloorSlabs[i],
            data.elements.groundFloorSlabs,
            draft.presets.elements.groundFloorSlabs,
            draft.presets.uValues.groundFloorSlabs,
            data.uValues.groundFloorSlabs,
          );
        }
      }
    }

    // Helper function to update exterior walls above terrain
    function updateExteriorWallsAboveTerrain(target: any, data: LcaBuildingElementPresetDTO) {
      if (!target.exteriorWallsCladdingAboveTerrain) {
        target.exteriorWallsCladdingAboveTerrain = [{} as ExteriorWallsAboveTerrainContainer];
      }
      if (data.data.exteriorWallsCladdingAboveTerrain) {
        normalizeArrayLength(target.exteriorWallsCladdingAboveTerrain, data.data.exteriorWallsCladdingAboveTerrain);
      }
      for (let i = 0; i < target.exteriorWallsCladdingAboveTerrain.length; i++) {
        if (data.elements.exteriorWallsCladdingAboveTerrain) {
          updateValues(
            target.exteriorWallsCladdingAboveTerrain[i],
            data.elements.exteriorWallsCladdingAboveTerrain,
            draft.presets.elements.exteriorWallsCladdingAboveTerrain,
            draft.presets.uValues.exteriorWallsCladdingAboveTerrain,
            data.uValues.exteriorWallsCladdingAboveTerrain,
          );
          if (
            data.data.exteriorWallsCladdingAboveTerrain != null &&
            data.data.exteriorWallsCladdingAboveTerrain[i].orientation
          ) {
            target.exteriorWallsCladdingAboveTerrain[i].orientation =
              data.data.exteriorWallsCladdingAboveTerrain[i].orientation;
          }
        }
      }
    }

    // Helper function to update exterior walls under terrain
    function updateExteriorWallsUnderTerrain(target: any, data: LcaBuildingElementPresetDTO) {
      if (!target.exteriorWallsCladdingUnderTerrain) {
        target.exteriorWallsCladdingUnderTerrain = [{} as ExteriorWallsUnderTerrainContainer];
      }
      for (let i = 0; i < target.exteriorWallsCladdingUnderTerrain.length; i++) {
        if (data.elements.exteriorWallsCladdingUnderTerrain) {
          updateValues(
            target.exteriorWallsCladdingUnderTerrain[i],
            data.elements.exteriorWallsCladdingUnderTerrain,
            draft.presets.elements.exteriorWallsCladdingUnderTerrain,
            draft.presets.uValues.exteriorWallsCladdingUnderTerrain,
            data.uValues.exteriorWallsCladdingUnderTerrain,
          );
        }
      }
    }

    // Helper function to update interior walls above terrain
    function updateInteriorWallsAboveTerrain(target: any, data: LcaBuildingElementPresetDTO) {
      if (!target.interiorWallsAboveTerrain) {
        target.interiorWallsAboveTerrain = [{} as InteriorWallAboveTerrainContainer];
      }
      for (let i = 0; i < target.interiorWallsAboveTerrain.length; i++) {
        if (data.elements.interiorWallsAboveTerrain) {
          updateValues(
            target.interiorWallsAboveTerrain[i],
            data.elements.interiorWallsAboveTerrain,
            draft.presets.elements.interiorWallsAboveTerrain,
            null,
            null,
          );
        }
      }
    }

    // Helper function to update ceilings above terrain
    function updateCeilingsAboveTerrain(target: any, data: LcaBuildingElementPresetDTO) {
      if (!target.ceilingsAboveTerrain) {
        target.ceilingsAboveTerrain = [{} as CeilingAboveTerrainContainer];
      }
      for (let i = 0; i < target.ceilingsAboveTerrain.length; i++) {
        if (data.elements.ceilingsAboveTerrain) {
          updateValues(
            target.ceilingsAboveTerrain[i],
            data.elements.ceilingsAboveTerrain,
            draft.presets.elements.ceilingsAboveTerrain,
            null,
            null,
          );
        }
      }
    }

    // Helper function to update ceilings under terrain
    function updateCeilingsUnderTerrain(target: any, data: LcaBuildingElementPresetDTO) {
      if (!target.ceilingsUnderTerrain) {
        target.ceilingsUnderTerrain = [{} as CeilingUnderTerrainContainer];
      }
      for (let i = 0; i < target.ceilingsUnderTerrain.length; i++) {
        if (data.elements.ceilingsUnderTerrain) {
          updateValues(
            target.ceilingsUnderTerrain[i],
            data.elements.ceilingsUnderTerrain,
            draft.presets.elements.ceilingsUnderTerrain,
            draft.presets.uValues.ceilingsUnderTerrain,
            data.uValues.ceilingsUnderTerrain,
          );
        }
      }
    }

    // Helper function to update flat roofs above terrain
    function updateFlatRoofsAboveTerrain(target: any, data: LcaBuildingElementPresetDTO) {
      if (!target.flatRoofsAboveTerrain) {
        target.flatRoofsAboveTerrain = [{} as FlatRoofAboveTerrainContainer];
      }
      for (let i = 0; i < target.flatRoofsAboveTerrain.length; i++) {
        if (data.elements.flatRoofsAboveTerrain) {
          updateValues(
            target.flatRoofsAboveTerrain[i],
            data.elements.flatRoofsAboveTerrain,
            draft.presets.elements.flatRoofsAboveTerrain,
            draft.presets.uValues.flatRoofsAboveTerrain,
            data.uValues.flatRoofsAboveTerrain,
          );
        }
      }
    }

    // Helper function to update inclined roofs above terrain
    function updateInclinedRoofsAboveTerrain(target: any, data: LcaBuildingElementPresetDTO) {
      if (!target.inclinedRoofsAboveTerrain) {
        target.inclinedRoofsAboveTerrain = [{} as InclinedRoofAboveTerrainContainer];
      }
      for (let i = 0; i < target.inclinedRoofsAboveTerrain.length; i++) {
        if (data.elements.inclinedRoofsAboveTerrain) {
          updateValues(
            target.inclinedRoofsAboveTerrain[i],
            data.elements.inclinedRoofsAboveTerrain,
            draft.presets.elements.inclinedRoofsAboveTerrain,
            draft.presets.uValues.inclinedRoofsAboveTerrain,
            data.uValues.inclinedRoofsAboveTerrain,
          );
        }
      }
    }

    // Helper function to update roofs under terrain
    function updateRoofsUnderTerrain(target: any, data: LcaBuildingElementPresetDTO) {
      if (!target.roofsUnderTerrain) {
        target.roofsUnderTerrain = [{} as RoofUnderTerrainContainer];
      }
      for (let i = 0; i < target.roofsUnderTerrain.length; i++) {
        if (data.elements.roofsUnderTerrain) {
          updateValues(
            target.roofsUnderTerrain[i],
            data.elements.roofsUnderTerrain,
            draft.presets.elements.roofsUnderTerrain,
            draft.presets.uValues.roofsUnderTerrain,
            data.uValues.roofsUnderTerrain,
          );
        }
      }
    }

    // Helper function to update windows above terrain
    function updateWindowsAboveTerrain(target: any, data: LcaBuildingElementPresetDTO) {
      if (!target.windowsAboveTerrain) {
        target.windowsAboveTerrain = [{} as WindowAboveTerrainContainer];
      }
      if (data.data.windowsAboveTerrain) {
        normalizeArrayLength(target.windowsAboveTerrain, data.data.windowsAboveTerrain);
      }
      for (let i = 0; i < target.windowsAboveTerrain.length; i++) {
        if (data.elements.windowsAboveTerrain && data.data.windowsAboveTerrain![i] != null) {
          updateValues(
            target.windowsAboveTerrain[i],
            data.elements.windowsAboveTerrain,
            draft.presets.elements.windowsAboveTerrain,
            target.windowsAboveTerrain[i].uvaluePlaceholder,
            data.data.windowsAboveTerrain![i].uvaluePlaceholder,
          );
          target.windowsAboveTerrain[i].orientation = target.exteriorWallsCladdingAboveTerrain[i].orientation;
        }
      }
    }

    // Helper function to update windows under terrain
    function updateWindowsUnderTerrain(target: any, data: LcaBuildingElementPresetDTO) {
      if (!target.windowsUnderTerrain) {
        target.windowsUnderTerrain = [{} as WindowUnderTerrainContainer];
      }
      for (let i = 0; i < target.windowsUnderTerrain.length; i++) {
        if (data.elements.windowsUnderTerrain) {
          updateValues(
            target.windowsUnderTerrain[i],
            data.elements.windowsUnderTerrain,
            draft.presets.elements.windowsUnderTerrain,
            draft.presets.uValues.windowsUnderTerrain,
            data.data.windowsUnderTerrain![i].uvaluePlaceholder,
          );
        }
      }
    }

    // Helper function to update interior walls under terrain
    function updateInteriorWallsUnderTerrain(target: any, data: LcaBuildingElementPresetDTO) {
      if (!target.interiorWallsUnderTerrain) {
        target.interiorWallsUnderTerrain = [{} as InteriorWallUnderTerrainContainer];
      }
      for (let i = 0; i < target.interiorWallsUnderTerrain.length; i++) {
        if (data.elements.interiorWallsUnderTerrain) {
          updateValues(
            target.interiorWallsUnderTerrain[i],
            data.elements.interiorWallsUnderTerrain,
            draft.presets.elements.interiorWallsUnderTerrain,
            null,
            null,
          );
        }
      }
    }

    // Helper function to normalize array length
    function normalizeArrayLength<T>(targetArray: T[], sourceArray: T[]) {
      while (targetArray.length < sourceArray.length) {
        targetArray.push({} as T);
      }
      while (targetArray.length > sourceArray.length) {
        targetArray.pop();
      }
    }

    // Helper function to update values
    function updateValues(
      state: ContourLca,
      newState: ContourLca,
      previousPreset: ContourLca | undefined | null,
      previousPresetUValue: number | null | undefined,
      currentPresetUValue?: number | null | undefined,
    ) {
      if (state.base == null || previousPreset?.base == state.base) {
        state.base = newState.base;
      }
      if (state.isolation == null || previousPreset?.isolation == state.isolation) {
        state.isolation = newState.isolation;
      }
      if (state.surface == null || previousPreset?.surface == state.surface) {
        state.surface = newState.surface;
      }
      if (
        (state.orientation == null && newState.orientation != null) ||
        previousPreset?.orientation == state.orientation
      ) {
        state.orientation = newState.orientation;
      }
      if (state.uvaluePlaceholder == null || previousPresetUValue == state.uvaluePlaceholder) {
        state.uvaluePlaceholder = currentPresetUValue;
      }
    }
    // Set default heating type and distribution if not already set
    setDefaultHeatingType(draft);

    // Check if the facade type matches the target type
    const isEqual = checkFacadeTypeEquality(draft, data);

    if (isEqual) {
      // Update various elements based on the target type
      updateElements(draft, data);
    }

    // Update presets with the new data
    draft.presets = data;
  }),

  immerOn(RealEstateActions.presetLCAFunctionalUnits, (draft: RealEstateState, data) => {
    draft.realEstateProgress = data.data;
  }),

  immerOn(RealEstateActions.presetLCAOverhaulFunctionalUnits, (draft: RealEstateState, data) => {
    draft.realEstateProgress = data.data;
  }),

  immerOn(RealEstateActions.presetLCAFunctionalUnitsSuccess, (draft: RealEstateState, data) => {
    for (let i = 0; i < data.data.length; i++) {
      const value = data.data[i];

      // console.warn(`reduces fu success ${draft.realEstateProgress.usages[i].type == value.type}, ${value.numFuUsage}`);
      if (value.targetType == 'OVERHAUL') {
        draft.presets.targetType = 'OVERHAUL';
        if ((draft.realEstateProgress as RebuildEstateDTO).targetOverhaul.usages[i].type == value.type) {
          //data is from API - we have only numFuUsage there.
          (draft.realEstateProgress as RebuildEstateDTO).targetOverhaul.usages[i].numFuUsagePlaceholder =
            value.numFuUsagePlaceholder;
        }
      } else if (value.targetType == 'ADDITION_STORY') {
        draft.presets.targetType = 'ADDITION_STORY';
        (draft.realEstateProgress as RebuildEstateDTO).targetAdditionStory!.usages[i].numFuUsagePlaceholder =
          value.numFuUsagePlaceholder;
      } else if (value.targetType == 'ANNEX') {
        draft.presets.targetType = 'ANNEX';
        (draft.realEstateProgress as RebuildEstateDTO).targetAnnex!.usages[i].numFuUsagePlaceholder =
          value.numFuUsagePlaceholder;
      } else {
        delete draft.presets.targetType;
        if (draft.realEstateProgress.usages[i].type == value.type) {
          //data is from API - we have only numFuUsage there.
          draft.realEstateProgress.usages[i].numFuUsagePlaceholder = value.numFuUsagePlaceholder;
        }
      }
    }
  }),

  immerOn(RealEstateActions.presetLCAPv, (draft: RealEstateState, data) => {
    draft.realEstateProgress = data.data;
  }),

  immerOn(RealEstateActions.presetLCAPvSuccess, (draft: RealEstateState, data) => {
    if (
      draft.realEstateProgress != null &&
      draft.realEstateProgress.energy != null &&
      draft.realEstateProgress.energy.current != null
    ) {
      draft.realEstateProgress.energy.current.annualPvRoofProduction = data.pvRoof;

      if (draft.realEstateProgress.energy.target != null) {
        draft.realEstateProgress.energy.target!.annualPvRoofProduction = data.targetPvRoof;
      }
    }
  }),

  immerOn(RealEstateActions.presetLCAPvError, (draft: RealEstateState, data) => {
    console.warn('presetLCAPvError', data);
  }),

  immerOn(RealEstateActions.presetLCAHPPower, (draft: RealEstateState, data) => {
    // console.log('presetLCAHPPower', data.data);
    draft.realEstateProgress = data.data;
  }),

  immerOn(RealEstateActions.presetOverhaulLCAHPPower, (draft: RealEstateState, data) => {
    // console.log('presetLCAHPPower', data.data);
    draft.realEstateProgress = data.data;
  }),

  immerOn(RealEstateActions.presetLCAHPPowerSuccess, (draft: RealEstateState, data) => {
    //to calculate the effective value it would be as following:
    // data.data[0] / 100 * 7 = HP power in kW.
    draft.targetType = 'CURRENT';
    draft.realEstateProgress.energy.current.heatPowerPlaceholder = data.data.hp[0];
  }),

  immerOn(RealEstateActions.presetOverhaulLCAHPPowerSuccess, (draft: RealEstateState, data) => {
    //to calculate the effective value it would be as following:
    // data.data[0] / 100 * 7 = HP power in kW.
    draft.targetType = 'OVERHAUL';
    draft.realEstateProgress.energy.target!.heatPowerPlaceholder = data.data.hp[0];
  }),

  immerOn(RealEstateActions.estimateLCACostsSuccess, (draft: RealEstateState, data) => {
    // if (draft.estimationOutput == null) {
    draft.status = CALCULATION_STATE.FINAL_RESULT_LOADED;
    draft.estimationOutput = {
      lcaOutputs: { lcaOutputPlot: {}, lcaOutput: {}, lcaRebuildOutput: {}, lcaRebuildOutputPlot: {} },
    } as RebuildEstimationOutputDTO;
    // draft.estimationOutput = { lcaOutputPlot: {} } as RebuildEstimationOutputDTO;
    // } else {
    // draft.withdrawDTO = data;
    draft.withdrawDTO = {} as WithdrawDTO;
    draft.withdrawDTO.outputDTO = {} as OutputDTO;
    draft.withdrawDTO.outputDTO.lcaOutputs = {
      lcaOutput: {},
      lcaOutputPlot: {
        crremPlotCO2Dto: {} as any,
        crremPlotPNRDto: {} as any,
        energyPlotDTO: [],
        geakPlotDTO: [],
      },
      lcaRebuildOutput: {},
      lcaRebuildOutputPlot: {
        crremPlotCO2Dto: {} as any,
        crremPlotPNRDto: {} as any,
        energyPlotDTO: [],
        geakPlotDTO: [],
      },
    };
    draft.withdrawDTO.outputDTO.lcaOutputs.lcaOutputPlot = { ...data.outputDTO.lcaOutputs.lcaOutputPlot };
    draft.withdrawDTO.outputDTO.lcaOutputs.lcaRebuildOutputPlot = { ...data.outputDTO.lcaOutputs.lcaRebuildOutputPlot };
    draft.withdrawDTO.outputDTO.NK = { ...data.outputDTO.NK };
    draft.withdrawDTO.outputDTO.EK = { ...data.outputDTO.EK };
    draft.withdrawDTO.outputDTO.VK = { ...data.outputDTO.VK };
    draft.withdrawDTO.outputDTO.costs = data.outputDTO.costs ?? [];
    draft.withdrawDTO.outputDTO.lccCosts = data.outputDTO.lccCosts != null ? { ...data.outputDTO.lccCosts } : null;
    draft.withdrawDTO.outputDTO.insuranceCost = { ...data.outputDTO.insuranceCost };
    draft.withdrawDTO.outputDTO.smallBuildingCost = { ...data.outputDTO.smallBuildingCost };
    draft.withdrawDTO.outputDTO.isPomCosts = { ...data.outputDTO.isPomCosts };
    draft.withdrawDTO.outputDTO.pomCosts = { ...data.outputDTO.pomCosts };
    draft.withdrawDTO.outputDTO.times = { ...data.outputDTO.times };
    draft.withdrawDTO.realEstateId = data.realEstateId;
    draft.withdrawDTO.pdfId = data.pdfId;
    draft.withdrawDTO.pdfIdOfReleaseCandidate = data.pdfIdOfReleaseCandidate;
    draft.withdrawDTO.rebuildReplacementBuildingInformationDTO = data.rebuildReplacementBuildingInformationDTO;
    draft.withdrawDTO.errorMessage = data.errorMessage;
    (draft.realEstateProgress as OutRealEstateDTO).id = data.realEstateId;
    draft.estimationOutput.lcaOutputs = { ...data.outputDTO.lcaOutputs };
    // draft.estimationOutput.lcaOutput = data.lcaOutput;
    if (data.outputDTO.lcaOutputs.lcaOutput != null) {
      for (const [key, value] of Object.entries(data.outputDTO.lcaOutputs.lcaOutput)) {
        if (value.YEAR == null) {
          draft.withdrawDTO.outputDTO.lcaOutputs.lcaOutput[key as LcaOutputType] = { ...value, YEAR: getSum(value) };
        } else {
          draft.withdrawDTO.outputDTO.lcaOutputs.lcaOutput[key as LcaOutputType] = { ...value };
        }
      }
    }
    if (
      draft.realEstateProgress.metaData.tasks.includes('REBUILD_COSTS') &&
      data.outputDTO.lcaOutputs.lcaRebuildOutput != null
    ) {
      for (const [key, value] of Object.entries(data.outputDTO.lcaOutputs.lcaRebuildOutput)) {
        if (value.YEAR == null) {
          draft.withdrawDTO.outputDTO.lcaOutputs.lcaRebuildOutput[key as LcaOutputType] = {
            ...value,
            YEAR: getSum(value),
          };
        } else {
          draft.withdrawDTO.outputDTO.lcaOutputs.lcaRebuildOutput[key as LcaOutputType] = { ...value };
        }
      }
    }
    draft.withdrawDTO.outputDTO.lcaOutputs.lcaOutput.RENEWABLE_PRIMARY_ENERGY =
      data.outputDTO.lcaOutputs.lcaOutput.RENEWABLE_PRIMARY_ENERGY;
    draft.withdrawDTO.outputDTO.lcaOutputs.lcaOutput.NON_RENEWABLE_PRIMARY_ENERGY =
      data.outputDTO.lcaOutputs.lcaOutput.NON_RENEWABLE_PRIMARY_ENERGY;
    draft.withdrawDTO.outputDTO.lcaOutputs.lcaOutput.TOTAL_PRIMARY_ENERGY =
      data.outputDTO.lcaOutputs.lcaOutput.TOTAL_PRIMARY_ENERGY;
    draft.withdrawDTO.outputDTO.lcaOutputs.lcaOutput.CO_2_EMISSION = data.outputDTO.lcaOutputs.lcaOutput.CO_2_EMISSION;
    draft.withdrawDTO.outputDTO.lcaOutputs.lcaOutput.COST = data.outputDTO.lcaOutputs.lcaOutput.COST;
    draft.withdrawDTO.outputDTO.lcaOutputs.lcaOutput.RER = data.outputDTO.lcaOutputs.lcaOutput.RER;
    draft.withdrawDTO.outputDTO.lcaOutputs.lcaOutput.EPB_USES_OF_ELECTRICITY =
      data.outputDTO.lcaOutputs.lcaOutput.EPB_USES_OF_ELECTRICITY;
    draft.withdrawDTO.outputDTO.lcaOutputs.lcaOutput.PRODUCED_ELECTRICITY =
      data.outputDTO.lcaOutputs.lcaOutput.PRODUCED_ELECTRICITY;
    draft.withdrawDTO.outputDTO.lcaOutputs.lcaOutput.EPB_PRODUCED_AND_USED_ELECTRICITY =
      data.outputDTO.lcaOutputs.lcaOutput.EPB_PRODUCED_AND_USED_ELECTRICITY;
    draft.withdrawDTO.outputDTO.lcaOutputs.lcaOutput.ELECTRICITY_USED_FOR_BATTERY_LOADING =
      data.outputDTO.lcaOutputs.lcaOutput.ELECTRICITY_USED_FOR_BATTERY_LOADING;
    draft.withdrawDTO.outputDTO.lcaOutputs.lcaOutput.GRID_EXPORTED = data.outputDTO.lcaOutputs.lcaOutput.GRID_EXPORTED;
    draft.withdrawDTO.outputDTO.lcaOutputs.lcaOutput.CONTRIBUTION_BY_BATTERY =
      data.outputDTO.lcaOutputs.lcaOutput.CONTRIBUTION_BY_BATTERY;
    draft.withdrawDTO.outputDTO.lcaOutputs.lcaOutput.BATTERY_CHARGE_AFTER_CONTRIBUTION =
      data.outputDTO.lcaOutputs.lcaOutput.BATTERY_CHARGE_AFTER_CONTRIBUTION;
    draft.withdrawDTO.outputDTO.lcaOutputs.lcaOutput.GRID_DELIVERED_ELECTRICITY = {
      ...data.outputDTO.lcaOutputs.lcaOutput.GRID_DELIVERED_ELECTRICITY,
      YEAR: getSum(data.outputDTO.lcaOutputs.lcaOutput.GRID_DELIVERED_ELECTRICITY!),
    };
    draft.withdrawDTO.outputDTO.lcaOutputs.lcaOutput.CONTRIBUTION = data.outputDTO.lcaOutputs.lcaOutput.CONTRIBUTION;
    draft.withdrawDTO.outputDTO.lcaOutputs.lcaOutput.FRACTION_CONTRIBUTION =
      data.outputDTO.lcaOutputs.lcaOutput.FRACTION_CONTRIBUTION;
    draft.withdrawDTO.outputDTO.lcaOutputs.lcaOutput.DHW = {
      ...data.outputDTO.lcaOutputs.lcaOutput.DHW,
      YEAR: getSum(data.outputDTO.lcaOutputs.lcaOutput.DHW!),
    };
    draft.withdrawDTO.outputDTO.lcaOutputs.lcaOutput.LIGHTING = {
      ...data.outputDTO.lcaOutputs.lcaOutput.LIGHTING,
      YEAR: getSum(data.outputDTO.lcaOutputs.lcaOutput.LIGHTING!),
    };
    draft.withdrawDTO.outputDTO.lcaOutputs.lcaOutput.APPLIANCES = {
      ...data.outputDTO.lcaOutputs.lcaOutput.APPLIANCES,
      YEAR: getSum(data.outputDTO.lcaOutputs.lcaOutput.APPLIANCES!),
    };
    draft.withdrawDTO.outputDTO.lcaOutputs.lcaOutput.TRANSPORT = {
      ...data.outputDTO.lcaOutputs.lcaOutput.TRANSPORT,
      YEAR: getSum(data.outputDTO.lcaOutputs.lcaOutput.TRANSPORT!),
    };
    draft.withdrawDTO.outputDTO.lcaOutputs.lcaOutput.ENERGY_SUM = {
      ...data.outputDTO.lcaOutputs.lcaOutput.ENERGY_SUM,
      YEAR: getSum(data.outputDTO.lcaOutputs.lcaOutput.ENERGY_SUM!),
    };
    draft.withdrawDTO.outputDTO.lcaOutputs.lcaOutput.PRODUCED_ELECTRICITY = {
      ...data.outputDTO.lcaOutputs.lcaOutput.PRODUCED_ELECTRICITY,
      YEAR: getSum(data.outputDTO.lcaOutputs.lcaOutput.PRODUCED_ELECTRICITY!),
    };
    draft.withdrawDTO.outputDTO.lcaOutputs.lcaOutput.NON_EPB = data.outputDTO.lcaOutputs.lcaOutput.NON_EPB;
    draft.withdrawDTO.outputDTO.lcaOutputs.lcaOutput.SOLAR_PRODUCIBILITY =
      data.outputDTO.lcaOutputs.lcaOutput.SOLAR_PRODUCIBILITY;
    draft.withdrawDTO.outputDTO.lcaOutputs.lcaOutput.PV = data.outputDTO.lcaOutputs.lcaOutput.PV;
    // }
    draft.greyEnergy = aggregateGreyEnergyData(data.outputDTO.lcaOutputs.lcaOutput);
    if (
      draft.realEstateProgress.metaData.tasks.includes('REBUILD_COSTS') &&
      data.outputDTO.lcaOutputs.lcaRebuildOutput != null
    ) {
      draft.rebuildGreyEnergy = aggregateGreyEnergyData(data.outputDTO.lcaOutputs.lcaRebuildOutput);
    }
    // draft.status = CALCULATION_STATE.FINAL_RESULT_LOADED;
  }),

  immerOn(RealEstateActions.estimateLCACostsError, (draft: RealEstateState, data) => {
    console.warn('estimateLCACostsError');
    draft.status = CALCULATION_STATE.ERROR;
    draft.estimationOutput = {} as RebuildEstimationOutputDTO;
    draft.withdrawDTO = {} as WithdrawDTO;
    Sentry.addBreadcrumb({ message: `estimateLCACostsError:`, level: 'error' });
    Sentry.captureException(data.error);
    Sentry.getCurrentScope().addAttachment({
      filename: draft.realEstateProgress.metaData.name + '-' + new Date() + '.json',
      data: JSON.stringify(draft.realEstateProgress),
    });
  }),

  immerOn(RealEstateActions.estimateLCAEnergyNeeds, (draft: RealEstateState, data) => {
    // console.log('estimateLCAEnergyNeeds', data.data.usages);
    if (draft.status != CALCULATION_STATE.CALCULATING && draft.status != CALCULATION_STATE.FINAL_RESULT_LOADED) {
      draft.status = CALCULATION_STATE.CALLING;
      draft.realEstateProgress = data.data;
    }
    // console.log('estimateLCAEnergyNeeds', draft.realEstateProgress.usages);
  }),
  immerOn(RealEstateActions.estimateLCAEnergyNeedsFailure, (draft: RealEstateState, data) => {
    //we can handle error here.
    draft.status = CALCULATION_STATE.READY;
  }),
  immerOn(RealEstateActions.estimateLCAEnergyNeedsSuccess, (draft: RealEstateState, data) => {
    console.log('estimateLCAEnergyNeedsSuccess', data);

    const targetType = data.lcaTargetType;
    draft.status = CALCULATION_STATE.READY;
    // console.warn('EstimateLCAEnergyNeedsSuccess', data);
    if (draft.estimationOutput == null) {
      // draft.estimationOutput = { lcaOutput: {} } as RebuildEstimationOutputDTO;
      draft.estimationOutput = {
        lcaOutputs: { lcaOutputPlot: {}, lcaOutput: {}, lcaRebuildOutput: {}, lcaRebuildOutputPlot: {} },
      } as RebuildEstimationOutputDTO;
    }
    draft.estimationOutput.lcaOutputs = { ...data.output.lcaOutputs };
    draft.estimationOutput.lcaTargetType = data.lcaTargetType;
    // console.warn('EstimateLCAEnergyNeedsSuccess: ', data.lcaOutput.SENSIBLE_ENERGY_NEED_FOR_HEATING);
    if (
      data.output.lcaOutputs.lcaOutput.SENSIBLE_ENERGY_NEED_FOR_HEATING &&
      data.output.lcaOutputs.lcaOutput.SENSIBLE_ENERGY_NEED_FOR_HEATING.YEAR
    ) {
      draft.realEstateProgress.energy.current.annualHeatingNeedsPlaceholder = Math.round(
        data.output.lcaOutputs.lcaOutput.SENSIBLE_ENERGY_NEED_FOR_HEATING.YEAR,
      );
    }
    if (
      data.output.lcaOutputs.lcaOutput.SENSIBLE_ENERGY_NEED_FOR_COOLING &&
      data.output.lcaOutputs.lcaOutput.SENSIBLE_ENERGY_NEED_FOR_COOLING.YEAR
    ) {
      draft.realEstateProgress.energy.current.annualCoolingNeedsPlaceholder = Math.round(
        data.output.lcaOutputs.lcaOutput.SENSIBLE_ENERGY_NEED_FOR_COOLING.YEAR,
      );
    }
    if (
      data.output.lcaOutputs.lcaOutput.MONTHLY_LATENT_ENERGY_NEEDS_FOR_DEHUMIDIFICATION &&
      data.output.lcaOutputs.lcaOutput.MONTHLY_LATENT_ENERGY_NEEDS_FOR_DEHUMIDIFICATION.YEAR
    ) {
      //Sum up all values inside the following object data.lcaOutputs.lcaOutput.ANNUAL_LATENT_ENERGY_NEEDS_FOR_DE_HUMIDIFICATION and round the value
      draft.realEstateProgress.energy.current.annualDehumidificationNeedsPlaceholder = Math.round(
        // Object.values(data.lcaOutputs.lcaOutput.MONTHLY_LATENT_ENERGY_NEEDS_FOR_DEHUMIDIFICATION).reduce((a, b) => a + b, 0),
        // );
        data.output.lcaOutputs.lcaOutput.MONTHLY_LATENT_ENERGY_NEEDS_FOR_DEHUMIDIFICATION.YEAR,
      );
    }
    if (
      data.output.lcaOutputs.lcaOutput.MONTHLY_LATENT_ENERGY_NEED_FOR_HUMIDIFICATION &&
      data.output.lcaOutputs.lcaOutput.MONTHLY_LATENT_ENERGY_NEED_FOR_HUMIDIFICATION.YEAR
    ) {
      draft.realEstateProgress.energy.current.annualHumidificationNeedsPlaceholder = Math.round(
        // Object.values(data.lcaOutputs.lcaOutput.MONTHLY_LATENT_ENERGY_NEED_FOR_HUMIDIFICATION).reduce((a, b) => a + b, 0),
        data.output.lcaOutputs.lcaOutput.MONTHLY_LATENT_ENERGY_NEED_FOR_HUMIDIFICATION.YEAR,
      );
    }
  }),

  immerOn(RealEstateActions.estimateOverhaulLCAEnergyNeedsSuccess, (draft: RealEstateState, data) => {
    draft.status = CALCULATION_STATE.READY;
    if (draft.estimationOutput == null) {
      draft.estimationOutput = {
        lcaOutputs: { lcaOutputPlot: {}, lcaOutput: {}, lcaRebuildOutput: {}, lcaRebuildOutputPlot: {} },
      } as RebuildEstimationOutputDTO;
    }
    draft.estimationOutput.lcaOutputs = { ...data.output.lcaOutputs };
    draft.estimationOutput.lcaTargetType = data.lcaTargetType;
    if (
      data.output.lcaOutputs.lcaOutput.SENSIBLE_ENERGY_NEED_FOR_HEATING &&
      data.output.lcaOutputs.lcaOutput.SENSIBLE_ENERGY_NEED_FOR_HEATING.YEAR
    ) {
      draft.realEstateProgress.energy.target!.annualHeatingNeedsPlaceholder = Math.round(
        data.output.lcaOutputs.lcaOutput.SENSIBLE_ENERGY_NEED_FOR_HEATING.YEAR,
      );
    }
    if (
      data.output.lcaOutputs.lcaOutput.SENSIBLE_ENERGY_NEED_FOR_COOLING &&
      data.output.lcaOutputs.lcaOutput.SENSIBLE_ENERGY_NEED_FOR_COOLING.YEAR
    ) {
      draft.realEstateProgress.energy.target!.annualCoolingNeedsPlaceholder = Math.round(
        data.output.lcaOutputs.lcaOutput.SENSIBLE_ENERGY_NEED_FOR_COOLING.YEAR,
      );
    }
    if (
      data.output.lcaOutputs.lcaOutput.MONTHLY_LATENT_ENERGY_NEEDS_FOR_DEHUMIDIFICATION &&
      data.output.lcaOutputs.lcaOutput.MONTHLY_LATENT_ENERGY_NEEDS_FOR_DEHUMIDIFICATION.YEAR
    ) {
      draft.realEstateProgress.energy.target!.annualDehumidificationNeedsPlaceholder = Math.round(
        data.output.lcaOutputs.lcaOutput.MONTHLY_LATENT_ENERGY_NEEDS_FOR_DEHUMIDIFICATION.YEAR,
      );
    }
    if (
      data.output.lcaOutputs.lcaOutput.MONTHLY_LATENT_ENERGY_NEED_FOR_HUMIDIFICATION &&
      data.output.lcaOutputs.lcaOutput.MONTHLY_LATENT_ENERGY_NEED_FOR_HUMIDIFICATION.YEAR
    ) {
      draft.realEstateProgress.energy.target!.annualHumidificationNeedsPlaceholder = Math.round(
        data.output.lcaOutputs.lcaOutput.MONTHLY_LATENT_ENERGY_NEED_FOR_HUMIDIFICATION.YEAR,
      );
    }
  }),

  immerOn(RealEstateActions.estimateLCAEnergyNeedsAndPerformance, (draft: RealEstateState, data) => {
    if (draft.status != CALCULATION_STATE.CALCULATING && draft.status != CALCULATION_STATE.FINAL_RESULT_LOADED) {
      draft.status = CALCULATION_STATE.CALLING;
      // draft.realEstateProgress = { ...data.data };
      draft.realEstateProgress = data.data;
    }
  }),
  immerOn(RealEstateActions.estimateLCAEnergyNeedsAndPerformanceSuccess, (draft: RealEstateState, data) => {
    if (draft.status != CALCULATION_STATE.CALCULATING && draft.status != CALCULATION_STATE.FINAL_RESULT_LOADED) {
      draft.status = CALCULATION_STATE.READY;
      // draft.realEstateProgress = { ...data.data };

      //only write the data to the inner fields of estimateOutput if inner object has values loop through the object and check if there are values
      const targetType = data.data.lcaTargetType;
      draft.realEstateProgress.targetType = targetType;

      if (draft.estimationOutput == null) {
        draft.estimationOutput = data.data;
      }
      if (draft.estimationOutput.lcaOutputs == null) {
        draft.estimationOutput.lcaOutputs = {} as LcaOutput;
      }
      if (draft.estimationOutput.lcaOutputs.lcaOutputPlot == null) {
        draft.estimationOutput.lcaOutputs.lcaOutputPlot = {
          crremPlotCO2Dto: {} as any,
          crremPlotPNRDto: {} as any,
          energyPlotDTO: [],
          geakPlotDTO: [],
        };
      }
      if (draft.estimationOutput.lcaOutputs.lcaRebuildOutputPlot == null) {
        draft.estimationOutput.lcaOutputs.lcaRebuildOutputPlot = {
          crremPlotCO2Dto: {} as any,
          crremPlotPNRDto: {} as any,
          energyPlotDTO: [],
          geakPlotDTO: [],
        };
      }
      if (draft.estimationOutput.lcaOutputs.lcaOutput == null) {
        draft.estimationOutput.lcaOutputs.lcaOutput = {} as { [P in LcaOutputType]?: { [P in LcaValueType]?: number } };
      }
      if (draft.estimationOutput.lcaOutputs.lcaRebuildOutput == null) {
        draft.estimationOutput.lcaOutputs.lcaRebuildOutput = {} as {
          [P in LcaOutputType]?: { [P in LcaValueType]?: number };
        };
      }
      if (data.data.output.lcaOutputs.lcaOutput != null) {
        try {
          var lcaOutput = data.data.output.lcaOutputs.lcaOutput;
          draft.estimationOutput.lcaOutputs.lcaOutputPlot = data.data.output.lcaOutputs.lcaOutputPlot;
          draft.estimationOutput.lcaOutputs.lcaOutput.RENEWABLE_PRIMARY_ENERGY = lcaOutput.RENEWABLE_PRIMARY_ENERGY;
          draft.estimationOutput.lcaOutputs.lcaOutput.NON_RENEWABLE_PRIMARY_ENERGY =
            lcaOutput.NON_RENEWABLE_PRIMARY_ENERGY;
          draft.estimationOutput.lcaOutputs.lcaOutput.TOTAL_PRIMARY_ENERGY = lcaOutput.TOTAL_PRIMARY_ENERGY;
          draft.estimationOutput.lcaOutputs.lcaOutput.CO_2_EMISSION = lcaOutput.CO_2_EMISSION;
          draft.estimationOutput.lcaOutputs.lcaOutput.COST = lcaOutput.COST;
          draft.estimationOutput.lcaOutputs.lcaOutput.RER = lcaOutput.RER;
          draft.estimationOutput.lcaOutputs.lcaOutput.EPB_USES_OF_ELECTRICITY = lcaOutput.EPB_USES_OF_ELECTRICITY;
          draft.estimationOutput.lcaOutputs.lcaOutput.PRODUCED_ELECTRICITY = lcaOutput.PRODUCED_ELECTRICITY;
          draft.estimationOutput.lcaOutputs.lcaOutput.EPB_PRODUCED_AND_USED_ELECTRICITY =
            lcaOutput.EPB_PRODUCED_AND_USED_ELECTRICITY;
          draft.estimationOutput.lcaOutputs.lcaOutput.ELECTRICITY_USED_FOR_BATTERY_LOADING =
            lcaOutput.ELECTRICITY_USED_FOR_BATTERY_LOADING;
          draft.estimationOutput.lcaOutputs.lcaOutput.GRID_EXPORTED = lcaOutput.GRID_EXPORTED;
          draft.estimationOutput.lcaOutputs.lcaOutput.CONTRIBUTION_BY_BATTERY = lcaOutput.CONTRIBUTION_BY_BATTERY;
          draft.estimationOutput.lcaOutputs.lcaOutput.BATTERY_CHARGE_AFTER_CONTRIBUTION =
            lcaOutput.BATTERY_CHARGE_AFTER_CONTRIBUTION;
          draft.estimationOutput.lcaOutputs.lcaOutput.GRID_DELIVERED_ELECTRICITY = {
            ...lcaOutput.GRID_DELIVERED_ELECTRICITY,
            YEAR: getSum(lcaOutput.GRID_DELIVERED_ELECTRICITY!),
          };
          draft.estimationOutput.lcaOutputs.lcaOutput.CONTRIBUTION = lcaOutput.CONTRIBUTION;
          draft.estimationOutput.lcaOutputs.lcaOutput.FRACTION_CONTRIBUTION = lcaOutput.FRACTION_CONTRIBUTION;
          draft.estimationOutput.lcaOutputs.lcaOutput.DHW = {
            ...lcaOutput.DHW,
            YEAR: getSum(lcaOutput.DHW!),
          };
          draft.estimationOutput.lcaOutputs.lcaOutput.LIGHTING = {
            ...lcaOutput.LIGHTING,
            YEAR: getSum(lcaOutput.LIGHTING!),
          };
          draft.estimationOutput.lcaOutputs.lcaOutput.APPLIANCES = {
            ...lcaOutput.APPLIANCES,
            YEAR: getSum(lcaOutput.APPLIANCES!),
          };
          draft.estimationOutput.lcaOutputs.lcaOutput.TRANSPORT = {
            ...lcaOutput.TRANSPORT,
            YEAR: getSum(lcaOutput.TRANSPORT!),
          };
          draft.estimationOutput.lcaOutputs.lcaOutput.ENERGY_SUM = {
            ...lcaOutput.ENERGY_SUM,
            YEAR: getSum(lcaOutput.ENERGY_SUM!),
          };

          draft.estimationOutput.lcaOutputs.lcaOutput.NON_EPB = lcaOutput.NON_EPB;
          draft.estimationOutput.lcaOutputs.lcaOutput.SOLAR_PRODUCIBILITY = lcaOutput.SOLAR_PRODUCIBILITY;
          draft.estimationOutput.lcaOutputs.lcaOutput.PV = lcaOutput.PV;
          // draft.estimationOutput.lcaOutputs.lcaOutput.SENSIBLE_ENERGY_NEED_FOR_HEATING =
          //   lcaOutput.SENSIBLE_ENERGY_NEED_FOR_HEATING;
          // if (
          //   lcaOutput.SENSIBLE_ENERGY_NEED_FOR_HEATING &&
          //   draft.estimationOutput.lcaOutputs.lcaOutput &&
          //   draft.estimationOutput.lcaOutputs.lcaOutput.SENSIBLE_ENERGY_NEED_FOR_HEATING
          // ) {
          //   // draft.estimationOutput.lcaOutputs.lcaOutput.SENSIBLE_ENERGY_NEED_FOR_HEATING.YEAR = 0;
          //   const year = Object.values(lcaOutput.SENSIBLE_ENERGY_NEED_FOR_HEATING).reduce((a, b) => a + b, 0);
          //   // draft.estimationOutput.lcaOutputs.lcaOutput.SENSIBLE_ENERGY_NEED_FOR_HEATING.YEAR = year;
          //   console.log(year);
          // }
          // }

          // console.warn('estimateLCAEnergyNeedsAndPerformanceSuccess: ', data.data.lcaOutput.SENSIBLE_ENERGY_NEED_FOR_HEATING);
          // let lcaOutput = data.data.lcaOutput;

          if (lcaOutput.TRANSPORT) {
            draft.realEstateProgress.energy.current.annualTransportNeedsPlaceholder = Math.round(
              Object.values(lcaOutput.TRANSPORT).reduce((a, b) => a + b, 0),
            );
          }

          if (lcaOutput.LIGHTING) {
            draft.realEstateProgress.energy.current.annualLightningNeedsPlaceholder = Math.round(
              Object.values(lcaOutput.LIGHTING).reduce((a, b) => a + b, 0),
            );
          }

          if (lcaOutput.APPLIANCES) {
            draft.realEstateProgress.energy.current.annualApplianceNeedsPlaceholder = Math.round(
              Object.values(lcaOutput.APPLIANCES).reduce((a, b) => a + b, 0),
            );
          }
          if (lcaOutput.DHW) {
            draft.realEstateProgress.energy.current.annualDhwNeedsPlaceholder = Math.round(
              Object.values(lcaOutput.DHW).reduce((a, b) => a + b, 0),
            );
          }
          //Currently we don't calculate anything here. But this might change in the future.
          if (lcaOutput.NON_EPB) {
            draft.realEstateProgress.energy.current.annualNonEPBNeedsPlaceholder = Math.round(
              Object.values(lcaOutput.NON_EPB).reduce((a, b) => a + b, 0),
            );
          }
        } catch (error) {
          console.error('estimateLCAEnergyNeedsAndPerformanceSuccess', error);
        }
      }
    }
  }),

  immerOn(RealEstateActions.estimateOverhaulLCAEnergyNeedsAndPerformanceSuccess, (draft: RealEstateState, data) => {
    if (draft.status != CALCULATION_STATE.CALCULATING && draft.status != CALCULATION_STATE.FINAL_RESULT_LOADED) {
      draft.status = CALCULATION_STATE.READY;
      // draft.realEstateProgress = { ...data.data };

      //only write the data to the inner fields of estimateOutput if inner object has values loop through the object and check if there are values
      const targetType = data.data.lcaTargetType;
      draft.realEstateProgress.targetType = targetType;

      if (draft.estimationOutput == null) {
        draft.estimationOutput = data.data;
      }
      if (draft.estimationOutput.lcaOutputs == null) {
        draft.estimationOutput.lcaOutputs = {} as LcaOutput;
      }
      if (draft.estimationOutput.lcaOutputs.lcaOutputPlot == null) {
        draft.estimationOutput.lcaOutputs.lcaOutputPlot = {
          crremPlotCO2Dto: {} as any,
          crremPlotPNRDto: {} as any,
          energyPlotDTO: [],
          geakPlotDTO: [],
        };
      }
      if (draft.estimationOutput.lcaOutputs.lcaRebuildOutputPlot == null) {
        draft.estimationOutput.lcaOutputs.lcaRebuildOutputPlot = {
          crremPlotCO2Dto: {} as any,
          crremPlotPNRDto: {} as any,
          energyPlotDTO: [],
          geakPlotDTO: [],
        };
      }
      if (data.data.output.lcaOutputs.lcaOutput != null) {
        try {
          var lcaOutput = data.data.output.lcaOutputs.lcaOutput;
          draft.estimationOutput.lcaOutputs.lcaOutputPlot = data.data.output.lcaOutputs.lcaOutputPlot;
          draft.estimationOutput.lcaOutputs.lcaOutput.RENEWABLE_PRIMARY_ENERGY = lcaOutput.RENEWABLE_PRIMARY_ENERGY;
          draft.estimationOutput.lcaOutputs.lcaOutput.NON_RENEWABLE_PRIMARY_ENERGY =
            lcaOutput.NON_RENEWABLE_PRIMARY_ENERGY;
          draft.estimationOutput.lcaOutputs.lcaOutput.TOTAL_PRIMARY_ENERGY = lcaOutput.TOTAL_PRIMARY_ENERGY;
          draft.estimationOutput.lcaOutputs.lcaOutput.CO_2_EMISSION = lcaOutput.CO_2_EMISSION;
          draft.estimationOutput.lcaOutputs.lcaOutput.COST = lcaOutput.COST;
          draft.estimationOutput.lcaOutputs.lcaOutput.RER = lcaOutput.RER;
          draft.estimationOutput.lcaOutputs.lcaOutput.EPB_USES_OF_ELECTRICITY = lcaOutput.EPB_USES_OF_ELECTRICITY;
          draft.estimationOutput.lcaOutputs.lcaOutput.PRODUCED_ELECTRICITY = lcaOutput.PRODUCED_ELECTRICITY;
          draft.estimationOutput.lcaOutputs.lcaOutput.EPB_PRODUCED_AND_USED_ELECTRICITY =
            lcaOutput.EPB_PRODUCED_AND_USED_ELECTRICITY;
          draft.estimationOutput.lcaOutputs.lcaOutput.ELECTRICITY_USED_FOR_BATTERY_LOADING =
            lcaOutput.ELECTRICITY_USED_FOR_BATTERY_LOADING;
          draft.estimationOutput.lcaOutputs.lcaOutput.GRID_EXPORTED = lcaOutput.GRID_EXPORTED;
          draft.estimationOutput.lcaOutputs.lcaOutput.CONTRIBUTION_BY_BATTERY = lcaOutput.CONTRIBUTION_BY_BATTERY;
          draft.estimationOutput.lcaOutputs.lcaOutput.BATTERY_CHARGE_AFTER_CONTRIBUTION =
            lcaOutput.BATTERY_CHARGE_AFTER_CONTRIBUTION;
          draft.estimationOutput.lcaOutputs.lcaOutput.GRID_DELIVERED_ELECTRICITY = {
            ...lcaOutput.GRID_DELIVERED_ELECTRICITY,
            YEAR: getSum(lcaOutput.GRID_DELIVERED_ELECTRICITY!),
          };
          draft.estimationOutput.lcaOutputs.lcaOutput.CONTRIBUTION = lcaOutput.CONTRIBUTION;
          draft.estimationOutput.lcaOutputs.lcaOutput.FRACTION_CONTRIBUTION = lcaOutput.FRACTION_CONTRIBUTION;
          draft.estimationOutput.lcaOutputs.lcaOutput.DHW = {
            ...lcaOutput.DHW,
            YEAR: getSum(lcaOutput.DHW!),
          };
          draft.estimationOutput.lcaOutputs.lcaOutput.LIGHTING = {
            ...lcaOutput.LIGHTING,
            YEAR: getSum(lcaOutput.LIGHTING!),
          };
          draft.estimationOutput.lcaOutputs.lcaOutput.APPLIANCES = {
            ...lcaOutput.APPLIANCES,
            YEAR: getSum(lcaOutput.APPLIANCES!),
          };
          draft.estimationOutput.lcaOutputs.lcaOutput.TRANSPORT = {
            ...lcaOutput.TRANSPORT,
            YEAR: getSum(lcaOutput.TRANSPORT!),
          };
          draft.estimationOutput.lcaOutputs.lcaOutput.ENERGY_SUM = {
            ...lcaOutput.ENERGY_SUM,
            YEAR: getSum(lcaOutput.ENERGY_SUM!),
          };

          draft.estimationOutput.lcaOutputs.lcaOutput.NON_EPB = lcaOutput.NON_EPB;
          draft.estimationOutput.lcaOutputs.lcaOutput.SOLAR_PRODUCIBILITY = lcaOutput.SOLAR_PRODUCIBILITY;
          draft.estimationOutput.lcaOutputs.lcaOutput.PV = lcaOutput.PV;

          if (lcaOutput.TRANSPORT) {
            draft.realEstateProgress.energy.target!.annualTransportNeedsPlaceholder = Math.round(
              Object.values(lcaOutput.TRANSPORT).reduce((a, b) => a + b, 0),
            );
          }

          if (lcaOutput.LIGHTING) {
            draft.realEstateProgress.energy.target!.annualLightningNeedsPlaceholder = Math.round(
              Object.values(lcaOutput.LIGHTING).reduce((a, b) => a + b, 0),
            );
          }

          if (lcaOutput.APPLIANCES) {
            draft.realEstateProgress.energy.target!.annualApplianceNeedsPlaceholder = Math.round(
              Object.values(lcaOutput.APPLIANCES).reduce((a, b) => a + b, 0),
            );
          }
          if (lcaOutput.DHW) {
            draft.realEstateProgress.energy.target!.annualDhwNeedsPlaceholder = Math.round(
              Object.values(lcaOutput.DHW).reduce((a, b) => a + b, 0),
            );
          }
          //Currently we don't calculate anything here. But this might change in the future.
          if (lcaOutput.NON_EPB) {
            draft.realEstateProgress.energy.target!.annualNonEPBNeedsPlaceholder = Math.round(
              Object.values(lcaOutput.NON_EPB).reduce((a, b) => a + b, 0),
            );
          }
        } catch (error) {
          console.error('estimateLCAEnergyNeedsAndPerformanceSuccess', error);
        }
      }
    }
  }),

  immerOn(RealEstateActions.estimateLCAEnergyPerformance, (draft: RealEstateState, data) => {
    if (draft.status != CALCULATION_STATE.CALCULATING && draft.status != CALCULATION_STATE.FINAL_RESULT_LOADED) {
      draft.status = CALCULATION_STATE.CALLING;
      draft.realEstateProgress = { ...data.data };
    }
  }),
  immerOn(
    RealEstateActions.estimateLCAEnergyPerformanceSuccess,
    (draft: RealEstateState, data: RebuildEstimationOutputDTO) => {
      draft.status = CALCULATION_STATE.READY;
      //only write the data to the inner fields of estimateOutput if inner object has values loop through the object and check if there are values
      var lcaOutput = data.lcaOutputs.lcaOutput;

      if (draft.estimationOutput == null) {
        draft.estimationOutput = data;
      } else {
        draft.estimationOutput.lcaOutputs.lcaOutputPlot = data.lcaOutputs.lcaOutputPlot;
        draft.estimationOutput.lcaOutputs.lcaOutput.RENEWABLE_PRIMARY_ENERGY = lcaOutput.RENEWABLE_PRIMARY_ENERGY;
        draft.estimationOutput.lcaOutputs.lcaOutput.NON_RENEWABLE_PRIMARY_ENERGY =
          lcaOutput.NON_RENEWABLE_PRIMARY_ENERGY;
        draft.estimationOutput.lcaOutputs.lcaOutput.TOTAL_PRIMARY_ENERGY = lcaOutput.TOTAL_PRIMARY_ENERGY;
        draft.estimationOutput.lcaOutputs.lcaOutput.CO_2_EMISSION = lcaOutput.CO_2_EMISSION;
        draft.estimationOutput.lcaOutputs.lcaOutput.COST = lcaOutput.COST;
        draft.estimationOutput.lcaOutputs.lcaOutput.RER = lcaOutput.RER;
        draft.estimationOutput.lcaOutputs.lcaOutput.EPB_USES_OF_ELECTRICITY = lcaOutput.EPB_USES_OF_ELECTRICITY;
        draft.estimationOutput.lcaOutputs.lcaOutput.PRODUCED_ELECTRICITY = lcaOutput.PRODUCED_ELECTRICITY;
        draft.estimationOutput.lcaOutputs.lcaOutput.EPB_PRODUCED_AND_USED_ELECTRICITY =
          lcaOutput.EPB_PRODUCED_AND_USED_ELECTRICITY;
        draft.estimationOutput.lcaOutputs.lcaOutput.ELECTRICITY_USED_FOR_BATTERY_LOADING =
          lcaOutput.ELECTRICITY_USED_FOR_BATTERY_LOADING;
        draft.estimationOutput.lcaOutputs.lcaOutput.GRID_EXPORTED = lcaOutput.GRID_EXPORTED;
        draft.estimationOutput.lcaOutputs.lcaOutput.CONTRIBUTION_BY_BATTERY = lcaOutput.CONTRIBUTION_BY_BATTERY;
        draft.estimationOutput.lcaOutputs.lcaOutput.BATTERY_CHARGE_AFTER_CONTRIBUTION =
          lcaOutput.BATTERY_CHARGE_AFTER_CONTRIBUTION;
        draft.estimationOutput.lcaOutputs.lcaOutput.GRID_DELIVERED_ELECTRICITY = {
          ...lcaOutput.GRID_DELIVERED_ELECTRICITY,
          YEAR: getSum(lcaOutput.GRID_DELIVERED_ELECTRICITY!),
        };
        draft.estimationOutput.lcaOutputs.lcaOutput.CONTRIBUTION = lcaOutput.CONTRIBUTION;
        draft.estimationOutput.lcaOutputs.lcaOutput.FRACTION_CONTRIBUTION = lcaOutput.FRACTION_CONTRIBUTION;
        draft.estimationOutput.lcaOutputs.lcaOutput.DHW = {
          ...lcaOutput.DHW,
          YEAR: getSum(lcaOutput.DHW!),
        };
        draft.estimationOutput.lcaOutputs.lcaOutput.LIGHTING = {
          ...lcaOutput.LIGHTING,
          YEAR: getSum(lcaOutput.LIGHTING!),
        };
        draft.estimationOutput.lcaOutputs.lcaOutput.APPLIANCES = {
          ...lcaOutput.APPLIANCES,
          YEAR: getSum(lcaOutput.APPLIANCES!),
        };
        draft.estimationOutput.lcaOutputs.lcaOutput.TRANSPORT = {
          ...lcaOutput.TRANSPORT,
          YEAR: getSum(lcaOutput.TRANSPORT!),
        };
        draft.estimationOutput.lcaOutputs.lcaOutput.ENERGY_SUM = {
          ...lcaOutput.ENERGY_SUM,
          YEAR: getSum(lcaOutput.ENERGY_SUM!),
        };
        draft.estimationOutput.lcaOutputs.lcaOutput.NON_EPB = lcaOutput.NON_EPB;
        draft.estimationOutput.lcaOutputs.lcaOutput.SOLAR_PRODUCIBILITY = lcaOutput.SOLAR_PRODUCIBILITY;
        draft.estimationOutput.lcaOutputs.lcaOutput.PV = lcaOutput.PV;
        // draft.estimationOutput.lcaOutputs.lcaOutput.SENSIBLE_ENERGY_NEED_FOR_HEATING =
        //   lcaOutput.SENSIBLE_ENERGY_NEED_FOR_HEATING;
        // if (
        //   lcaOutput.SENSIBLE_ENERGY_NEED_FOR_HEATING &&
        //   draft.estimationOutput.lcaOutputs.lcaOutput &&
        //   draft.estimationOutput.lcaOutputs.lcaOutput.SENSIBLE_ENERGY_NEED_FOR_HEATING
        // ) {
        //   // draft.estimationOutput.lcaOutputs.lcaOutput.SENSIBLE_ENERGY_NEED_FOR_HEATING.YEAR = 0;
        //   const year = Object.values(lcaOutput.SENSIBLE_ENERGY_NEED_FOR_HEATING).reduce((a, b) => a + b, 0);
        //   // draft.estimationOutput.lcaOutputs.lcaOutput.SENSIBLE_ENERGY_NEED_FOR_HEATING.YEAR = year;
        //   console.log(year);
        // }
      }

      if (lcaOutput.TRANSPORT) {
        draft.initialRealEstate.energy.current.annualTransportNeedsPlaceholder = Math.round(
          Object.values(lcaOutput.TRANSPORT).reduce((a, b) => a + b, 0),
        );
      }

      if (lcaOutput.LIGHTING) {
        draft.initialRealEstate.energy.current.annualLightningNeedsPlaceholder = Math.round(
          Object.values(lcaOutput.LIGHTING).reduce((a, b) => a + b, 0),
        );
      }

      if (lcaOutput.APPLIANCES) {
        draft.initialRealEstate.energy.current.annualApplianceNeedsPlaceholder = Math.round(
          Object.values(lcaOutput.APPLIANCES).reduce((a, b) => a + b, 0),
        );
      }
      if (lcaOutput.DHW) {
        draft.initialRealEstate.energy.current.annualDhwNeedsPlaceholder = Math.round(
          Object.values(lcaOutput.DHW).reduce((a, b) => a + b, 0),
        );
      }
      if (lcaOutput.PV) {
        draft.initialRealEstate.energy.current.annualPvRoofProduction = Math.round(
          Object.values(lcaOutput.PV).reduce((a, b) => a + b, 0),
        );
      }
    },
  ),
  immerOn(RealEstateActions.getLCAPlotCRREMCO2Success, (draft: RealEstateState, data) => {
    // blobToBase64(draft).then((base64) => {
    // console.log(base64);
    // draft.plots['CRREM_CO2'] = base64;
    // return RealEstateActions.getLCAPlotCRREMCO2Success(ret);
    // });
    if (data.targetType !== undefined) {
      if (draft.plotsRebuild === undefined) {
        draft.plotsRebuild = { CRREM_CO2: null, CRREM_NRP: null, ANNUAL_ENERGY: null, GEAK: null };
      }
      draft.plotsRebuild.CRREM_CO2 = data.base64;
    } else {
      if (draft.plots === undefined) {
        draft.plots = { CRREM_CO2: null, CRREM_NRP: null, ANNUAL_ENERGY: null, GEAK: null };
      }
      draft.plots.CRREM_CO2 = data.base64;
    }
  }),
  immerOn(RealEstateActions.getLCAPlotCRREMNonRenewableEnergySuccess, (draft: RealEstateState, data) => {
    if (data.targetType !== undefined) {
      if (draft.plotsRebuild === undefined) {
        draft.plotsRebuild = { CRREM_CO2: null, CRREM_NRP: null, ANNUAL_ENERGY: null, GEAK: null };
      }
      draft.plotsRebuild.CRREM_NRP = data.base64;
    } else {
      if (draft.plots === undefined) {
        draft.plots = { CRREM_CO2: null, CRREM_NRP: null, ANNUAL_ENERGY: null, GEAK: null };
      }
      draft.plots.CRREM_NRP = data.base64;
    }
  }),
  immerOn(RealEstateActions.getLCAPlotCRREMNonRenewableEnergyError, (draft: RealEstateState, data) => {
    if (draft.plots === undefined) {
      draft.plots = { CRREM_CO2: null, CRREM_NRP: null, ANNUAL_ENERGY: null, GEAK: null };
    }
    draft.plots.CRREM_NRP = null;
  }),
  immerOn(RealEstateActions.getLCAPlotAnnualEnergySuccess, (draft: RealEstateState, data) => {
    if (data.targetType !== undefined) {
      if (draft.realEstateProgress.metaData.tasks.includes('REBUILD_COSTS')) {
        if (draft.plotsRebuild === undefined) {
          draft.plotsRebuild = { CRREM_CO2: null, CRREM_NRP: null, ANNUAL_ENERGY: null, GEAK: null };
        }
        draft.plotsRebuild.ANNUAL_ENERGY = data.base64;
      }
    } else {
      if (draft.plots === undefined) {
        draft.plots = { CRREM_CO2: null, CRREM_NRP: null, ANNUAL_ENERGY: null, GEAK: null };
      }
      draft.plots.ANNUAL_ENERGY = data.base64;
    }
  }),
  immerOn(RealEstateActions.getLCAPlotAnnualEnergyError, (draft: RealEstateState, data) => {
    if (draft.plots === undefined) {
      draft.plots = { CRREM_CO2: null, CRREM_NRP: null, ANNUAL_ENERGY: null, GEAK: null };
    }
    draft.plots.ANNUAL_ENERGY = null;
    if (draft.plotsRebuild === undefined) {
      draft.plotsRebuild = { CRREM_CO2: null, CRREM_NRP: null, ANNUAL_ENERGY: null, GEAK: null };
    }
    draft.plotsRebuild.ANNUAL_ENERGY = null;
  }),
  immerOn(RealEstateActions.getLCAPlotGEAKSuccess, (draft: RealEstateState, data) => {
    if (draft.plots === undefined) {
      draft.plots = { CRREM_CO2: null, CRREM_NRP: null, ANNUAL_ENERGY: null, GEAK: null };
    }
    draft.plots.GEAK = data.base64;
  }),
  immerOn(RealEstateActions.getLCAPlotGEAKError, (draft: RealEstateState, data) => {
    if (draft.plots === undefined) {
      draft.plots = { CRREM_CO2: null, CRREM_NRP: null, ANNUAL_ENERGY: null, GEAK: null };
    }
    draft.plots.GEAK = null;
  }),
  immerOn(RealEstateActions.estimateLCAGreyEnergySuccess, (draft: RealEstateState, data) => {
    // console.log('grey Energy', data);
    //foreach field in data.lcaOutput add it to the draft.estimationOutput.lcaOutput if it isn't present
    //if it is present add the values to the existing values

    // { [P in LcaOutputType]?: { [P in LcaValueType]?: number } }
    for (const [key, value] of Object.entries(data)) {
      // if (
      //   key === 'ENERGY_SUM' ||
      //   key === 'NON_EPB' ||
      //   key === 'SOLAR_PRODUCIBILITY' ||
      //   key === 'ENERGY_SUM' ||
      //   key === 'NON_EPB' ||
      //   key === 'SOLAR_PRODUCIBILITY' ||
      //   key === 'ENERGY_SUM' ||
      //   key === 'NON_EPB' ||
      //   key === 'SOLAR_PRODUCIBILITY' ||
      //   key === 'ENERGY_SUM' ||
      //   key === 'NON_EPB' ||
      //   key === 'SOLAR_PRODUCIBILITY' ||
      //   key === 'ENERGY_SUM' ||
      //   key === 'NON_EPB' ||
      //   key === 'SOLAR_PRODUCIBILITY' ||
      //   key === 'ENERGY_SUM' ||
      //   key === 'NON_EPB' ||
      //   key === 'SOLAR_PRODUCIBILITY' ||
      //   key === 'ENERGY_SUM' ||
      //   key === 'NON_EPB' ||
      //   key === 'SOLAR_PRODUCIBILITY' ||
      //   key === 'ENERGY_SUM' ||
      //   key === 'NON_EPB' ||
      //   key === 'SOLAR_PRODUCIBILITY' ||
      //   key === 'ENERGY_SUM' ||
      //   key === 'NON_EPB' ||
      //   key === 'SOLAR_PRODUCIBILITY' ||
      //   key === 'ENERGY_SUM' ||
      //   key === 'NON_EPB' ||
      //   key === 'SOLAR_PRODUCIBILITY' ||
      //   key === 'ENERGY_SUM' ||
      //   key === 'NON_EPB' ||
      //   key === 'SOLAR_PRODUCIBILITY' ||
      //   key === 'ENERGY_SUM' ||
      //   key === 'NON_EPB' ||
      //   key === 'SOLAR_PRODUCIBILITY' ||
      //   key === 'ENERGY_SUM' ||
      //   key === 'NON_EPB' ||
      //   key === 'SOLAR_PRODUCIBILITY'
      // ) {
      draft.estimationOutput.lcaOutputs.lcaOutput[key as LcaOutputType] = value;
      // } else {
      //   console.log(`Key is: ${key}, Value is: ${value}`);
      // }
    }

    draft.greyEnergy = aggregateGreyEnergyData(data.lcaOutputs.lcaOutput);
  }),
  immerOn(RealEstateActions.estimateGeoMetrics, (draft: RealEstateState, data) => {
    draft.realEstateProgress = { ...data.data };
  }),
  immerOn(RealEstateActions.estimateGeoMetricsSuccess, (draft: RealEstateState, data) => {
    draft.realEstateProgress = { ...data.data };
  }),
  immerOn(RealEstateActions.updateTargetType, (draft: RealEstateState, data) => {
    draft.targetType = data.targetType;
  }),
);

export const realEstatesFeature = createFeature({
  name: realEstatesFeatureKey,
  reducer,
  // extraSelectors: ({ selectPresets, selectRealEstateProgress }) => ({
  //   selectPresetsWithRealEstate: createSelector(selectPresets, selectRealEstateProgress, (query, realEstate) => {
  //     {
  //       realEstate:realEstate,
  //         query:query,
  //     }
  //   })
  // }),

  extraSelectors: ({
    selectRealEstateProgress,
    selectPresets,
    selectStatus,
    selectEstimationOutput,
    selectTargetType,
  }) => {
    const selectPresetsWithRealEstate = createSelector(
      selectRealEstateProgress,
      selectPresets,
      (realEstate, presets) => {
        return { realEstate: realEstate, preset: presets };
      },
    );
    const selectRealEstateWithStatus = createSelector(
      selectRealEstateProgress,
      selectStatus,
      (selectRealEstateProgress, selectStatus) => ({ realEstate: selectRealEstateProgress, status: selectStatus }),
    );
    const selectRealEstateWithTargetType = createSelector(
      selectRealEstateProgress,
      selectPresets,
      selectTargetType,
      (selectRealEstateProgress, selectPresets, selectTargetType) => ({
        realEstate: selectRealEstateProgress,
        presets: selectPresets,
        targetType: selectTargetType,
      }),
    );
    const selectEstimationWithTargetType = createSelector(
      selectEstimationOutput,
      selectTargetType,
      (estimation, targetType) => ({ estimation, targetType }),
    );
    return {
      selectPresetsWithRealEstate,
      selectRealEstateWithStatus,
      selectRealEstateWithTargetType,
      selectEstimationWithTargetType,
    };
  },
});
// export const selectRealEstateWithStatus = createSelector(
//   realEstatesFeature.selectRealEstateProgress,
//   realEstatesFeature.selectStatus,
//   (selectRealEstateProgress, selectStatus) => ({ realEstate: selectRealEstateProgress, status: selectStatus }),
// );

export function injectRealEstateFeature() {
  const store = inject(Store);
  return {
    realEstate$: store.select(realEstatesFeature.selectInitialRealEstate),
  };
}

//other way of declaring effect for easier testability.
// export const loadEmptyRealEstateDto$ = createEffect(
//   (actions$ = inject(Actions), store = inject(Store), http = inject(HttpClient)) => {
//     // const http = inject(HttpClient);
//     return actions$.pipe(
//       ofType(RealEstateActions.getEmptyRealEstate),
//       withLatestFrom(store.select(realEstatesFeature.selectRealEstate)),
//       concatMap(([request, filters]) =>
//         http.get(`${environment.serverUrl}/api/rebuild_estate/empty`).pipe(
//           map(
//             (realestate: any) => {
//               realestate.metaData.withdrawalProductType = request.wpt;
//
//               realestate.metaData.tasks = request.tasks.length === 0 ? [request.wpt] : request.tasks;
//               // let clonedRealEstate = _.cloneDeep(realestate);
//               this.prepareRealEstateEmptyObject(realestate, request.wpt);
//               // console.error('prepareRealEstateEmptyObject: ' + JSON.stringify(clonedRealEstate));
//               return RealEstateActions.loadRealEstateSuccess(realestate);
//             },
//             catchError((error) => of(RealEstateActions.loadRealEstateFailure(error))),
//             // (),
//           ),
//         ),
//       ),
//     );
//   },
//   { functional: true },
// );
